import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import authService, { LoginRequest, LoginResponse, RegisterRequest } from '../services/api/auth.service';

interface AuthContextType {
  user: any | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  checkAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Initialize user from localStorage synchronously to prevent flash of limited menu
  const [user, setUser] = useState<any | null>(() => {
    return authService.getCurrentUser();
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = async () => {
    // Check if we have any tokens (access or refresh)
    const hasTokens = !!localStorage.getItem('access_token') || !!localStorage.getItem('refresh_token');

    if (!hasTokens) {
      // No tokens at all - user is definitely logged out
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
      return;
    }

    // We have tokens - try to fetch fresh profile (this will trigger token refresh if needed)
    try {
      const freshUser = await authService.getProfile();
      console.log('[AuthContext] Fresh user from API:', freshUser);
      localStorage.setItem('user', JSON.stringify(freshUser));
      setUser(freshUser);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('[AuthContext] Failed to fetch profile:', error);
      // API call failed - check if we have cached user data
      const cachedUser = authService.getCurrentUser();
      if (cachedUser) {
        console.log('[AuthContext] Using cached user:', cachedUser);
        setUser(cachedUser);
        setIsAuthenticated(true);
      } else {
        // No cached user and API failed - user needs to login
        setUser(null);
        setIsAuthenticated(false);
      }
    }

    setIsLoading(false);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (credentials: LoginRequest) => {
    try {
      const response: LoginResponse = await authService.login(credentials);
      setIsAuthenticated(true);

      // Fetch full profile to get all permissions (login response may not include them)
      try {
        const fullProfile = await authService.getProfile();
        localStorage.setItem('user', JSON.stringify(fullProfile));
        setUser(fullProfile);
      } catch (profileError) {
        // Fallback to login response user if profile fetch fails
        console.warn('[AuthContext] Could not fetch full profile after login:', profileError);
        setUser(response.user);
      }

      // Dispatch custom event to trigger form config fetch
      window.dispatchEvent(new Event('user-logged-in'));
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  const register = async (data: RegisterRequest) => {
    await authService.register(data);
    // Registration doesn't automatically log in
    // User needs admin approval first
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    loading: isLoading,
    login,
    logout,
    register,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;